import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import Layout from "../layout/Layout";
import SEO from "../Components/SEO/SEO";
import { projects } from "../data/siteData";

export default function WorkPage() {
  return (
    <Layout>
      <SEO
        title="Our Work | Case Studies | Quantum Spark Technology"
        description="Explore software projects delivered by Quantum Spark Technology — e-commerce, NFC digital products, IP marketplaces, and SaaS platforms for growing companies."
        path="/work"
      />

      <section className="border-b border-zinc-200 bg-[#FAFAFB] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-brand-purple">Case studies</p>
          <h1 className="mb-6 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-brand-ink md:text-5xl">
            Products we have helped ship
          </h1>
          <p className="max-w-2xl text-lg leading-relaxed text-brand-muted">
            From Korean skincare commerce to NFC identity platforms and IP marketplaces, our team delivers
            production-ready engineering, design, and QA for founders who need reliable partners — not pitch decks.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-10">
            {projects.map((project, i) => (
              <motion.article
                key={project.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="overflow-hidden rounded-3xl border border-zinc-200 bg-[#FAFAFB] lg:grid lg:grid-cols-[1.1fr_1fr]"
              >
                <div className="aspect-video lg:aspect-auto">
                  <img
                    src={project.coverImage}
                    alt={`${project.title} project screenshot`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col p-8 md:p-10">
                  <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-brand-purple">
                    {project.category}
                  </p>
                  <h2 className="mb-2 font-display text-2xl font-bold text-brand-ink">{project.title}</h2>
                  <p className="mb-4 text-sm font-medium text-brand-muted">Client: {project.client}</p>
                  <p className="mb-6 flex-1 leading-relaxed text-brand-muted">{project.description}</p>
                  <div className="mb-6 flex flex-wrap gap-2">
                    {project.tech.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  {project.url && (
                    <a
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-semibold text-brand-purple hover:underline"
                    >
                      Visit live site <ExternalLink size={14} className="ml-1" />
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-16 rounded-3xl border border-zinc-200 bg-[#FAFAFB] p-8 text-center md:p-12">
            <h2 className="mb-3 font-display text-2xl font-bold text-brand-ink">Want results like these?</h2>
            <p className="mx-auto mb-6 max-w-xl text-brand-muted">
              Tell us about your product goals. We will respond within 24 hours with next steps for a discovery call.
            </p>
            <Link to="/contact" className="font-semibold text-brand-purple hover:underline">
              Start a project →
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
